// Frame-buffered typed event bus. Systems emit facts; narrator/camera/UI
// glue subscribe. main.ts drains once per simulated frame (including death
// frames) so events never leak across frames.
//
// emit() pushes; drain() fires subscribers in order, then clears the buffer.
// Buffered (not immediate dispatch) keeps emitter and consumer call-stacks
// decoupled and ordering deterministic across systems within a frame.

import type { Entity } from './components'
import type { BossId } from './bosses'
import type { BiomeId } from './biomes'
import type { DoorKind } from './dungeon/types'
import type { MutationKind, PlayerForm } from './components'

export type GameEvent =
  | { type: 'creature.eaten';   eater: Entity;   victim: Entity; gained: number }
  | { type: 'predator.eaten';   predator: Entity }
  | { type: 'predator.spotted'; predator: Entity }
  | { type: 'predator.windup';  predator: Entity }
  | { type: 'biome.entered';    biome: BiomeId }
  | { type: 'orb.collected';    kind: 'dna' | 'mutation'; value: number }
  | { type: 'orb.deathOrbNearby' }
  | { type: 'damage.taken';     entity: Entity;  amount: number }
  | { type: 'damage.dealt';     target: Entity;  amount: number }
  | { type: 'player.lowHealth' }
  | { type: 'player.died' }
  | { type: 'ally.converted';   ally: Entity }
  | { type: 'ally.died';        count: number;   remaining: number }
  | { type: 'mutation.applied'; player: Entity;  kind: MutationKind; firstTime: boolean }
  | { type: 'mutation.capped' }
  | { type: 'skill.activated';  skill: 'size' }
  | { type: 'evolution.started';   tier: number }
  | { type: 'evolution.burst';     tier: number }
  | { type: 'evolution.completed'; tier: number }
  // The tier-3 speciation lock — fired by the lineage system at the burst
  // beat, the instant the critter form resolves. Juice + narrator subscribe.
  | { type: 'lineage.speciated';   form: PlayerForm; player: Entity }
  // Sealed doors (B3 shortcuts, B4 gates): opened fires once per door per
  // save; blocked fires on the first refused press of a still-closed door —
  // the narrator names the key, which is the telegraph.
  | { type: 'door.opened';      edge: number; kind: DoorKind; x: number; y: number }
  | { type: 'door.blocked';     edge: number; kind: DoorKind; keyId?: string }
  // Key items (B4): the deadpan traversal keys. Granted once, persisted.
  | { type: 'key.granted';      keyId: string }
  // Town portal (B5): cast = travel home leaving a linked gate pair;
  // return = home→field trip that closes the pair. (x,y) is the arrival
  // point — camera glue snaps there instead of lerping across the world.
  | { type: 'portal.cast';      x: number; y: number }
  | { type: 'portal.return';    x: number; y: number }
  // Daughter-cell checkpoint attuned (monorail-s8k): last-touched wins.
  | { type: 'checkpoint.attuned'; id: string; x: number; y: number }
  | { type: 'boss.spawned';     boss: Entity;    id: BossId }
  | { type: 'boss.wake';        boss: Entity }
  | { type: 'boss.phase2';      boss: Entity }
  | { type: 'boss.killed';      boss: Entity;    id: BossId }

export type GameEventType = GameEvent['type']
export type EventOf<T extends GameEventType> = Extract<GameEvent, { type: T }>
export type Unsub = () => void

export interface EventBus {
  emit(e: GameEvent): void
  drain(): readonly GameEvent[]
  subscribe<T extends GameEventType>(type: T, fn: (e: EventOf<T>) => void): Unsub
}

const EMPTY: readonly GameEvent[] = Object.freeze([])

export function createEventBus(): EventBus {
  const buffer: GameEvent[] = []
  const handlers = new Map<GameEventType, Array<(e: GameEvent) => void>>()

  return {
    emit(e) {
      buffer.push(e)
    },
    drain() {
      if (buffer.length === 0) return EMPTY
      // Splice the in-flight events out before dispatch so handlers that
      // emit are deferred to the next drain (preserves ordering, prevents
      // re-entrancy loops).
      const snapshot = buffer.splice(0, buffer.length)
      for (const e of snapshot) {
        const hs = handlers.get(e.type)
        if (!hs) continue
        for (const h of hs) h(e)
      }
      return snapshot
    },
    subscribe(type, fn) {
      let list = handlers.get(type)
      if (!list) {
        list = []
        handlers.set(type, list)
      }
      const wrapped = fn as (e: GameEvent) => void
      list.push(wrapped)
      return () => {
        const idx = list!.indexOf(wrapped)
        if (idx >= 0) list!.splice(idx, 1)
      }
    },
  }
}
